import React, { createContext, ReactNode, useState, useContext, useMemo } from "react";
import { conditionTypes, IRoutingRule, IRoutingRuleSet } from "./models/RoutingRuleSetModel";
import { shallowEqual, useSelector } from "react-redux";
import { RoutingRulesData } from "./redux/RoutingRulesSlice";
import { TestCase } from "./models/TestSuiteModel";
import { ArticleType, coverTypeGroupValueMap, getOtherConditionDropdownOptions } from "./models/RoutingRuleDropdownData";
import { ChangeType } from "./models/Common";
import { get } from "lodash";

export interface routingRuleContextProps extends RoutingRulesData {
    availableTestCases: TestCase[]
    changedTestCases: TestCase[]
    availableRoutingRules: IRoutingRule[]
    changedRoutingRules: IRoutingRule[]
    getArticleType: (article: string) => ArticleType | undefined,
    getProductOptionDropdownOptions: () => any[] | undefined,
    getConditionDropdownOptions: () => any[] | undefined,
    selectedRoutingRule: SelectedRoutingRule,
    setSelectedRoutingRule: (selectedRoutingRule: SelectedRoutingRule) => void,
    selectedTestCase: SelectedTestCase,
    setSelectedTestCase: (selectedTestCase: SelectedTestCase) => void,
    uiEvents: RoutingRuleUIEvents
}

type RoutingRuleUIEvents = {
    showRoutingRulesPage: () => void,
    showRuleDetailsModal: () => void,
    hideRuleDetailsModal: () => void,
    showRuleEditModal: () => void,
    hideRuleEditModal: () => void,
    showTestCasePage: () => void,
    showTestCaseEditModal: () => void,
    hideTestCaseEditModal: () => void,
    showTestCaseImportCompareModal: () => void,
    hideTestCaseImportCompareModal: () => void
}

export enum SelectionReason {
    Default = "default",
    New = "new",
    Clone = "clone"
}

type SelectedRoutingRule = {
    routingRule: IRoutingRule,
    selectionReason: SelectionReason
}

type SelectedTestCase = {
    testCase: TestCase,
    selectionReason: SelectionReason
}

type Lookup = { [key: string]: string }

const initialRoutingRule = {
    id: "",
    name: "",
    sequenceNumber: 0,
    conditions: {
        conditionType: conditionTypes.conditionGroup
    },
    decision: []
}

const initialRoutingRuleSelection = {
    routingRule: initialRoutingRule,
    selectionReason: SelectionReason.Default
} as SelectedRoutingRule

const initialTestCaseSelection = {
    testCase: {},
    selectionReason: SelectionReason.Default
} as SelectedTestCase

const initialRoutingRuleSet = {
} as IRoutingRuleSet

const initialState: routingRuleContextProps = {
    alert: null,
    articleTypes: [],
    brands: [],
    channels: [],
    productOptionValues: [],
    getArticleType: () => undefined,
    getProductOptionDropdownOptions: () => undefined,
    getConditionDropdownOptions: () => undefined,
    nrApiCallsInProgress: 0,
    routingRuleSet: initialRoutingRuleSet,
    importedRuleSet: initialRoutingRuleSet,
    availableRoutingRules: [],
    changedRoutingRules: [],
    selectedRoutingRule: initialRoutingRuleSelection,
    setSelectedRoutingRule: () => { },
    selectedTestCase: initialTestCaseSelection,
    setSelectedTestCase: () => { },
    testCases: [],
    availableTestCases: [],
    changedTestCases: [],
    importedTestCases: [],
    uiEvents: {
        showRoutingRulesPage: () => { },
        showRuleDetailsModal: () => { },
        hideRuleDetailsModal: () => { },
        showRuleEditModal: () => { },
        hideRuleEditModal: () => { },
        showTestCasePage: () => { },
        showTestCaseEditModal: () => { },
        hideTestCaseEditModal: () => { },
        showTestCaseImportCompareModal: () => { },
        hideTestCaseImportCompareModal: () => { }
    }
};

const RoutingRuleContext = createContext<routingRuleContextProps>(initialState);

export function useRoutingRuleContext() {
    return useContext(RoutingRuleContext);
}

type Props = {
    children: ReactNode,
    routingRulesUIEvents: RoutingRuleUIEvents
};

const RoutingRuleProvider = (props: Props) => {
    const [selectedRoutingRule, setSelectedRoutingRule] = useState<SelectedRoutingRule>(initialRoutingRuleSelection)
    const [selectedTestCase, setSelectedTestCase] = useState<SelectedTestCase>(initialTestCaseSelection)

    const { routingRulesData } = useSelector(
        (state: any) => ({
            routingRulesData: state.routingRulesData as RoutingRulesData
        }),
        shallowEqual
    );

    const articleTypeLookups = useMemo(() => {
        const articleTypeLookups: Lookup = {}
        routingRulesData.articleTypes.forEach((at) => at.articles.forEach((a) => articleTypeLookups[a.toLowerCase()] = at.name))
        return articleTypeLookups
    }, [routingRulesData.articleTypes])

    const getArticleType = (article: string) => {
        if (!article) {
            return undefined
        }
        const articleTypeName = articleTypeLookups[article.toLowerCase()] ?? ""
        return routingRulesData.articleTypes.find(at => at.name === articleTypeName)
    }

    const getProductOptionDropdownOptions = () => {
        const modifiedProductOptions = routingRulesData.productOptionValues.map(option => {

            const values = (option.optionName === "CoverType" && Array.isArray(option.optionValues))
                ? groupEvaluationValues(option.optionValues, coverTypeGroupValueMap)
                : option.optionValues;


            return {
                variableName: option.optionName,
                evaluationValues: values
            };
        });
        return modifiedProductOptions;
    }

    const getConditionDropdownOptions = () => {
        var otherConditionDropdownOptions = getOtherConditionDropdownOptions();
        const modifiedProductOptions = getProductOptionDropdownOptions() || [];
        return [...otherConditionDropdownOptions, ...modifiedProductOptions];
    }

    function groupEvaluationValues(existing: string[], groupValues: Record<string, string[]>): string[] {
        const childToGroup: Record<string, string> = {};
        Object.entries(groupValues).forEach(([group, children]) => {
            children.forEach(child => {
                childToGroup[child] = group;
            });
        });
        const groupBuckets: Record<string, string[]> = {};
        Object.keys(groupValues).forEach(group => groupBuckets[group] = []);
        const undefinedBucket: string[] = [];
        existing.forEach(value => {
            if (childToGroup[value]) {
                groupBuckets[childToGroup[value]].push(value);
            } else if (!Object.keys(groupValues).includes(value)) {
                undefinedBucket.push(value);
            }
        });
        const result: string[] = [];
        Object.keys(groupValues).forEach(group => {
            if (groupBuckets[group].length > 0) {
                result.push(group, ...groupBuckets[group]);
            }
        });
        if (undefinedBucket.length > 0) {
            result.push("group:Undefined", ...undefinedBucket);
        }
        return result;
    }


    const availableTestCases = routingRulesData.testCases.filter((test) => test.testCaseChange?.changeType !== ChangeType.Deleted) ?? []
    const changedTestCases = routingRulesData.testCases.filter((test) => test.testCaseChange
        //Deleted WITH an original is an actual delete, deleted without an original means a new rule that was deleted, thus no change
        && !(test.testCaseChange.changeType === ChangeType.Deleted && !test.testCaseChange.originalTestCase))
        ?? []

    const availableRoutingRules = routingRulesData.routingRuleSet.decisionRules.filter((rule) => rule.routingRuleChange?.changeType !== ChangeType.Deleted) ?? []
    const changedRoutingRules = routingRulesData.routingRuleSet.decisionRules.filter((rule) => rule.routingRuleChange
        //Deleted WITH an original is an actual delete, deleted without an original means a new rule that was deleted, thus no change
        && !(rule.routingRuleChange.changeType === ChangeType.Deleted && !rule.routingRuleChange.originalRoutingRule))
        ?? []

    const value = {
        ...routingRulesData,
        availableTestCases,
        changedTestCases,
        getArticleType,
        getProductOptionDropdownOptions,
        getConditionDropdownOptions,
        availableRoutingRules,
        changedRoutingRules,
        selectedRoutingRule,
        setSelectedRoutingRule,
        selectedTestCase,
        setSelectedTestCase,
        uiEvents: props.routingRulesUIEvents
    };
    return <RoutingRuleContext.Provider value={value}>{props.children}</RoutingRuleContext.Provider>;
}

export default RoutingRuleProvider;